/**
 * #1006 — the Tracker-side wiring for Polymarket US in "Cancel all": the
 * confirmation's US lines, the sweep, and its per-order report. The rules
 * (scope, exclusions, outcomes) live in `usBulkCancelModel.ts`.
 */
import { useCallback, useRef, useState } from 'react';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import { US_CANCEL_UNCONFIRMED_TEXT } from '@/lib/polymarketUs/cancelOutcome';
import {
  describeUsExclusions,
  readUsBulkInventory,
  runUsBulkCancel,
  usVisibleInventory, usVisibleInventoryAll,
  type UsBulkClients,
  type UsBulkInventory,
  type UsBulkResult,
} from './usBulkCancelModel';
import { readUsSessionKey } from './usTrackerFeed';
import type { VenueReadiness } from './venueReadiness';

export type UsBulkPrepared =
  | { phase: 'off' }
  /** Cancel visible: frozen from the US section's rows, no read. */
  | { phase: 'visible'; inventory: UsBulkInventory; unclassified: number }
  /** US has accounts to show but its account read is still loading or failed:
   * nothing can be frozen, and the dialog must say US is left out. */
  | { phase: 'not-ready'; loading: boolean; reason: string | null }
  | { phase: 'reading' }
  | { phase: 'ready'; inventory: UsBulkInventory }
  | { phase: 'unavailable'; code: string };

const OFF: UsBulkPrepared = { phase: 'off' };
const code = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

/**
 * `prepare` reads and freezes the US part when a Cancel all confirmation
 * opens; `discard` drops it (Back, or a new confirmation); `run` sends the
 * frozen targets once. Every prepared state and report is tagged with the
 * session key it was read under, and a changed key hides it.
 */
/** Fresh clients per read and per sweep, separate from the US section's: an
 * abandoned read (Back) settling late resets only its own client's selection,
 * never a later read's or a running sweep's. */
const readClients = (): UsBulkClients => ({ accounts: createUsBrowserAccountClient(), portfolio: createUsBrowserPortfolioClient() });

export function useUsBulkCancel(sessionKey: string, us: VenueReadiness) {
  const usReadable = us.readable;
  const usNotConfigured = us.state === 'not-configured';
  const usLoading = us.state === 'loading';
  const usReason = us.reason;
  const generation = useRef(0);
  const running = useRef(false);
  const [prepared, setPrepared] = useState<{ key: string; id: number; value: UsBulkPrepared }>({ key: '', id: 0, value: OFF });
  const [report, setReport] = useState<{ key: string; results: readonly UsBulkResult[]; note?: string } | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);

  const prepare = useCallback(() => {
    const id = ++generation.current;
    setReport(null);
    if (!usReadable) {
      // Not configured: no US account, nothing to say. Loading or failed: US
      // may hold orders, so the confirmation names it as left out.
      setPrepared({ key: sessionKey, id, value: usNotConfigured ? OFF
        : { phase: 'not-ready', loading: usLoading, reason: usReason } });
      return;
    }
    setPrepared({ key: sessionKey, id, value: { phase: 'reading' } });
    const current = () => generation.current === id && readUsSessionKey() === sessionKey;
    readUsBulkInventory(readClients(), Date.now(), current).then(
      (inventory) => {
        if (!current()) return;
        setPrepared({ key: sessionKey, id, value: { phase: 'ready', inventory } });
      },
      (error) => { if (current()) setPrepared({ key: sessionKey, id, value: { phase: 'unavailable', code: code(error) } }); },
    );
  }, [sessionKey, usReadable, usNotConfigured, usLoading, usReason]);

  /** Cancel visible: freeze what the US section shows now. */
  const prepareVisible = useCallback((shown: readonly Parameters<typeof usVisibleInventory>[0][] | null) => {
    const id = ++generation.current;
    setReport(null);
    if (!shown || shown.length === 0) {
      setPrepared({ key: sessionKey, id, value: OFF });
      return;
    }
    const { inventory, unclassified } = usVisibleInventoryAll(shown, Date.now());
    setPrepared({ key: sessionKey, id, value: inventory.targets.length || inventory.excluded.length
      ? { phase: 'visible', inventory, unclassified } : OFF });
  }, [sessionKey]);

  /** Confirmed while the US read was still running: the K/P sweep goes ahead
   * without US, and the report says so (Brendan's review: a slow US read must
   * not hold up Kalshi/Polymarket). The read is dropped; nothing US is sent. */
  const skipReading = useCallback(() => {
    ++generation.current;
    setPrepared({ key: sessionKey, id: generation.current, value: OFF });
    setReport({ key: sessionKey, results: [],
      note: 'Polymarket US was not included: its orders were still being read when you confirmed. Reopen Cancel all to include them.' });
  }, [sessionKey]);

  const discard = useCallback(() => {
    const id = ++generation.current;
    setPrepared({ key: sessionKey, id, value: OFF });
  }, [sessionKey]);

  /** Sends the frozen US targets captured by the last `prepare`, once. */
  const run = useCallback(async (inventory: UsBulkInventory) => {
    if (running.current || inventory.targets.length === 0) return;
    running.current = true;
    setSweeping(true);
    ++generation.current;
    setPrepared({ key: sessionKey, id: generation.current, value: OFF });
    try {
      const results = await runUsBulkCancel({ portfolio: createUsBrowserPortfolioClient() }, inventory.targets,
        () => readUsSessionKey() === sessionKey);
      if (readUsSessionKey() === sessionKey) setReport({ key: sessionKey, results });
    } finally {
      running.current = false;
      setSweeping(false);
      setRefreshSignal((n) => n + 1);
    }
  }, [sessionKey]);

  const value = prepared.key === sessionKey ? prepared.value : OFF;
  const current = report?.key === sessionKey ? report : null;
  return {
    prepared: value,
    /** Frozen targets, or none while reading/unavailable/off. */
    frozen: value.phase === 'ready' || value.phase === 'visible' ? value.inventory : null,
    reading: value.phase === 'reading',
    sweeping,
    report: current ? current.results : null,
    reportNote: current?.note ?? null,
    dismissReport: () => setReport(null),
    refreshSignal,
    prepare,
    prepareVisible,
    skipReading,
    discard,
    run,
  };
}

/** US lines in a bulk-cancel confirmation. */
export function UsBulkCancelLines({ prepared, waitsForUs = false }: {
  prepared: UsBulkPrepared;
  /** #1034: nothing else to send, so the confirm waits for this read. */
  waitsForUs?: boolean;
}) {
  const box = 'mt-2 space-y-0.5 text-xs';
  if (prepared.phase === 'off') return null;
  if (prepared.phase === 'not-ready') {
    return (
      <div className={box} data-testid="us-bulk-lines">
        <p className="text-amber-300 light:text-amber-700">
          {prepared.loading
            ? 'Polymarket US is still being checked, so no US orders are included. Go back and reopen Cancel all to include any.'
            : `Polymarket US could not be checked, so no US orders are included.${prepared.reason ? ` ${prepared.reason}` : ''}`}
        </p>
      </div>
    );
  }
  if (prepared.phase === 'reading') {
    return (
      <div className={box} data-testid="us-bulk-lines">
        <p className="text-muted-foreground">Polymarket US: reading your saved orders…</p>
        <p className="text-muted-foreground">
          {waitsForUs
            ? 'There is nothing else to cancel, so the button unlocks once your Polymarket US orders are read.'
            : 'If you confirm before this finishes, Polymarket US orders are not included in this sweep.'}
        </p>
      </div>
    );
  }
  if (prepared.phase === 'unavailable') {
    return (
      <div className={box} data-testid="us-bulk-lines">
        <p className="text-amber-300 light:text-amber-700">
          Polymarket US: orders could not be read (<span className="font-mono">{prepared.code}</span>) — none are included.
        </p>
      </div>
    );
  }
  const { targets, excluded, accounts } = prepared.inventory;
  const visible = prepared.phase === 'visible';
  return (
    <div className={box} data-testid="us-bulk-lines">
      <p className="text-foreground">
        {targets.length > 0
          ? `Polymarket US: ${targets.length} ${visible ? 'visible ' : ''}order${targets.length === 1 ? '' : 's'}${visible ? ' in the account shown' : ` across ${accounts} account${accounts === 1 ? '' : 's'}`}, sent one at a time.`
          : `Polymarket US: no cancellable ${visible ? 'visible ' : ''}orders of yours.`}
      </p>
      <p className="text-muted-foreground">
        {visible
          ? 'Your orders shown in the Polymarket US section under your filters — other US accounts and hidden orders keep resting.'
          : 'Your orders only — teammates’ orders on shared accounts are not included. Listed from G1’s saved records; orders placed outside G1 are not listed.'}
      </p>
      {visible && prepared.unclassified > 0 && (
        <p className="text-amber-300 light:text-amber-700">
          {`Includes ${prepared.unclassified} unclassified US order${prepared.unclassified === 1 ? '' : 's'} — market type not known yet, so the filters can't tell what ${prepared.unclassified === 1 ? 'it is' : 'they are'}.`}
        </p>
      )}
      {describeUsExclusions(excluded).map((line) => (
        <p key={line} className="text-amber-300 light:text-amber-700">{line}</p>
      ))}
      {targets.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {targets.map((t) => (
            <li key={t.key}>
              <span className="text-muted-foreground font-semibold">US</span>{' '}
              <span className="text-foreground break-all font-mono">{t.label}</span>
              <span className="text-muted-foreground"> (account …{t.accountId.slice(-4)})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const OUTCOME_TEXT: Record<UsBulkResult['outcome'], string> = {
  requested: 'Cancel requested — awaiting confirmation',
  unconfirmed: US_CANCEL_UNCONFIRMED_TEXT,
  failed: 'Cancel didn’t go out',
  'not-sent': 'Not sent — the login or organization changed',
};

/** Each US order's own result. Nothing here says "cancelled": an acknowledged
 * cancel is not a cancelled order, and unconfirmed is not failed.
 * `statusHint` names where Check status is from the host page (#1016: Game
 * Detail has no US section, so it points to Positions). */
export function UsBulkCancelReport({ results, note = null, statusHint = 'Use Check status in the Polymarket US section before cancelling again.' }:
  { results: readonly UsBulkResult[]; note?: string | null; statusHint?: string }) {
  if (results.length === 0 && note) {
    return (
      <div className="mt-2 border-border border-t pt-2 text-xs text-amber-300 light:text-amber-700" data-testid="us-bulk-report">{note}</div>
    );
  }
  const count = (o: UsBulkResult['outcome']) => results.filter((r) => r.outcome === o).length;
  const parts = [
    `${count('requested')} cancel requested`,
    count('unconfirmed') ? `${count('unconfirmed')} unconfirmed` : null,
    count('failed') ? `${count('failed')} didn’t go out` : null,
    count('not-sent') ? `${count('not-sent')} not sent` : null,
  ].filter(Boolean);
  return (
    <div className="mt-2 border-border border-t pt-2" data-testid="us-bulk-report">
      <div className="font-medium">Polymarket US: {parts.join(' · ')}</div>
      {/* A sweep can name many orders; the report scrolls instead of growing
          past the viewport. The account tail tells same-market rows apart. */}
      <div className="max-h-[40vh] overflow-y-auto">
        {results.filter((r) => r.outcome !== 'requested').map((r) => (
          <div
            key={r.target.key}
            data-testid={`us-bulk-result-${r.outcome}`}
            className={`mt-1 text-xs ${r.outcome === 'failed' ? 'text-red-300' : 'text-amber-300'}`}
          >
            <span className="font-semibold">US</span> <span className="break-all font-mono">{r.target.label}</span>
            <span className="text-muted-foreground"> (account …{r.target.accountId.slice(-4)})</span> — {OUTCOME_TEXT[r.outcome]}
            {r.code ? <span className="break-all font-mono"> ({r.code})</span> : null}
          </div>
        ))}
      </div>
      {count('unconfirmed') > 0 && (
        <div className="text-muted-foreground mt-1 text-xs">{statusHint}</div>
      )}
    </div>
  );
}
